import { useEffect, useMemo, useState } from 'react';
import type { Invoice } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { DataState } from '../../components/ui/DataState.js';
import { useGenerateProrateInvoice, useProrateInvoicePreview } from '../invoices/api.js';

interface ProrateInvoiceModalProps {
  isOpen: boolean;
  agreementId: string;
  onClose: () => void;
  onCreated: (invoice: Invoice) => void;
}

const money = (value: string): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value));

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Generates a one-off prorated draft invoice for an agreement whose first cycle (or newly
 * added lines) started mid-billing-cycle. Shows the prorate math before creating.
 */
export function ProrateInvoiceModal({
  isOpen,
  agreementId,
  onClose,
  onCreated,
}: ProrateInvoiceModalProps): JSX.Element {
  const previewQuery = useProrateInvoicePreview(agreementId, isOpen);
  const generateMutation = useGenerateProrateInvoice();
  const [invoiceDate, setInvoiceDate] = useState(today());

  const resetMutation = generateMutation.reset;
  useEffect(() => {
    if (isOpen) {
      setInvoiceDate(today());
      resetMutation();
    }
  }, [isOpen, agreementId, resetMutation]);

  const preview = previewQuery.data;
  const generalError = useMemo(() => {
    const err = generateMutation.error;
    if (err instanceof ApiError) return err.message;
    if (err) return 'Failed to generate the prorate invoice.';
    return null;
  }, [generateMutation.error]);

  const handleGenerate = (): void => {
    generateMutation.mutate(
      { agreementId, input: { invoiceDate } },
      { onSuccess: (invoice) => onCreated(invoice) },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate prorate invoice"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={generateMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            isLoading={generateMutation.isPending}
            disabled={!preview?.eligible || !invoiceDate}
          >
            Generate draft invoice
          </Button>
        </>
      }
    >
      <DataState
        isLoading={previewQuery.isLoading}
        isError={previewQuery.isError}
        error={previewQuery.error}
        onRetry={() => void previewQuery.refetch()}
        isEmpty={false}
      >
        {preview ? (
          <div className="flex flex-col gap-4">
            {!preview.eligible ? (
              <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-700">
                {preview.message ?? 'Nothing to prorate on this agreement.'}
              </p>
            ) : null}

            {preview.lines.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Prorated line</th>
                      <th className="px-4 py-2 text-right font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {preview.lines.map((line, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-slate-700">{line.description}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-slate-900">
                          {money(line.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-slate-500">
                        Total
                      </td>
                      <td className="px-4 py-2 text-right font-semibold tabular-nums text-slate-900">
                        {money(preview.total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : null}

            {preview.hoursProration ? (
              <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
                <p className="font-medium">First-month included hours (prorated)</p>
                <p className="mt-1">
                  {preview.hoursProration.proratedHoursIncluded} of{' '}
                  {preview.hoursProration.fullHoursIncluded} hours included for{' '}
                  {preview.hoursProration.periodLabel} ({preview.hoursProration.remainingDays} of{' '}
                  {preview.hoursProration.daysInMonth} days). Overage starts once usage exceeds{' '}
                  {preview.hoursProration.proratedHoursIncluded} hours that first month; the full{' '}
                  {preview.hoursProration.fullHoursIncluded} hours are included every cycle after.
                </p>
              </div>
            ) : null}

            <TextField
              label="Invoice date"
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
            />

            {generalError ? <p className="text-sm text-rose-600">{generalError}</p> : null}
          </div>
        ) : null}
      </DataState>
    </Modal>
  );
}
